import {
  Component,
  viewChild,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { SAP_OUTAGE_TABLE_DEFINITION } from '../definitions/sap-outage-table-definition';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { TabService } from 'src/app/core/services/tab.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { ReportingApiService } from 'src/app/services/api/reporting-api.service';
import { ReportSapOutage } from 'src/app/model/reporting/report-sap-outage';
import { ReportSapOutageTableService } from 'src/app/services/table/report-sap-outage-table.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { GeneralGridComponent } from 'src/app/core/components/general-grid.component';
import { AorApiService } from 'src/app/services/api/aor-api.service';
import { AOR } from 'src/app/model/locations/aor';
import { ReadOnlySapViewComponent } from '../components/read-only-sap-view/read-only-sap-view.component';
import { SapOutage } from 'src/app/model/sap/sap-outage';
import { SapApiService } from 'src/app/services/api/sap-api.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata, SelectItem } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { Panel } from '@openng/optimus-ui/panel';
import { NgClass } from '@angular/common';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { FormsModule } from '@angular/forms';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { GeneralGridComponent as GeneralGridComponent_1 } from '../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { dynamicSort } from 'src/app/core/functions/common-functions';

@Component({
  selector: 'app-sap-outage-report',
  templateUrl: './sap-outage-report.component.html',
  styleUrl: './sap-outage-report.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    Panel,
    NgClass,
    DatePicker,
    FormsModule,
    MultiSelect,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent_1,
    Dialog,
    ReadOnlySapViewComponent,
    TranslatePipe,
  ],
})
export class SapOutageReportComponent
  extends SearchContainerComponent<ReportSapOutage>
  implements OnInit
{
  protected _authApi = inject(AuthApiService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _contactApi = inject(ContactApiService);
  private _aorAPI = inject(AorApiService);
  protected _reportingApi = inject(ReportingApiService);
  protected _reportSapOutageTableService = inject(ReportSapOutageTableService);
  private _sapApi = inject(SapApiService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  readonly generalGrid = viewChild<GeneralGridComponent>('grid');
  readonly readOnlyDetailedView =
    viewChild<ReadOnlySapViewComponent>('detailedView');

  detailDialogVisible: boolean = false;
  outagesLoading: boolean = false;
  startDate: Date = new Date();
  endDate: Date | null = null;

  selectedBusinessUnits: string[] = [];

  aors: SelectItem[] = [];
  selectedAORs: SelectItem[] = [];

  availableOutageGroups: SelectItem[] = [];
  selectedOutageGroups: SelectItem[] = [];

  selectedOutage: SapOutage | null = null;

  isFirstLoad = true;

  todaySelected = false;
  tomorrowSelected = false;
  sevenDaysSelected = false;
  deratesSelected = false;
  pipelineMaintenanceSelected = false;

  private _isRefreshing = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._reportSapOutageTableService);
    this.initialize();
  }

  initialize() {
    this.columns = SAP_OUTAGE_TABLE_DEFINITION;
    this.elements = [];

    this.startDate = new Date();

    this.populateAors();

    this._sapApi.getOutageGroups().subscribe((data) => {
      this.availableOutageGroups = data
        .map((c) => ({
          label: c.name,
          value: c.CompanyCodes.map((c) => c.companyCode),
        }))
        .sort(dynamicSort('label', 1));

      this.refresh();
    });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    if (!this.startDate) {
      this.startDate = new Date();
    }

    const query = {
      reqStart: this.startDate
        ? this.createUTCDateEquivalent(this.startDate)
        : null,
      reqEnd: this.endDate ? this.createUTCDateEquivalent(this.endDate) : null,
      notifNum: filters.notifNum
        ? (filters.notifNum as FilterMetadata).value.trim()
        : null,
      flocNumber: filters.flocNumber
        ? (filters.flocNumber as FilterMetadata).value.trim()
        : null,
      funcLocDesc: filters.funcLocDesc
        ? (filters.funcLocDesc as FilterMetadata).value.trim()
        : null,
      shortDesc: filters.shortDesc
        ? (filters.shortDesc as FilterMetadata).value.trim()
        : null,
      taskText: filters.taskText
        ? (filters.taskText as FilterMetadata).value.trim()
        : null,
      longText: filters.longText
        ? (filters.longText as FilterMetadata).value.trim()
        : null,
      companyCodes: this.selectedOutageGroups
        ? this.selectedOutageGroups.flat()
        : null,
      aors: this.selectedAORs ? this.selectedAORs : null,
      useDefaultDateRange: this.isFirstLoad,
      derates: this.deratesSelected,
      pipelineMaintenance: this.pipelineMaintenanceSelected,
    };

    this.isFirstLoad = false;

    this._reportSapOutageTableService.setLastQuery(query);
    this.queryNetwork(req, $event, query);
  }

  createUTCDateEquivalent(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
      )
    );
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      let field = $event.lazy.sortField as string;

      sort[field] =
        $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'DESC' : 'ASC';
      this._reportSapOutageTableService.setLastSortField(field);
      this._reportSapOutageTableService.setLastSortDirection(
        $event.lazy.sortOrder as number
      );
    } else {
      sort['reqStart'] = 'DESC';
      this._reportSapOutageTableService.setLastSortField('reqStart');
      this._reportSapOutageTableService.setLastSortDirection(0);
    }

    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._reportingApi.queryForSapOutages(query, sort).subscribe({
        next: (data: ReportSapOutage[]) => {
          this.elements = [...data];
          this.totalRecords = this.elements.length;
          this.loading = false;
          this._isRefreshing = false;
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'REPORTING.MESSAGES.ERROR.QUERY_OUTAGES',
          });
          this.loading = false;
          this._isRefreshing = false;
        },
      });
    }
  }

  dateUpdate(fromEnd = false) {
    this.clearSelectedButtons();

    if (!this.endDate || (this.endDate < this.startDate && fromEnd === false)) {
      this.endDate = new Date(this.startDate.valueOf());
      this.endDate.setDate(this.endDate.getDate() + 1);
    } else if (this.endDate < this.startDate && fromEnd === true) {
      this.startDate = new Date(this.endDate.valueOf());
      this.startDate.setDate(this.startDate.getDate() - 1);
    }

    this.refresh();
  }

  populateAors() {
    this.aors = [];
    this._aorAPI
      .getAORs(this.selectedBusinessUnits)
      .subscribe((aors: AOR[]) => {
        this.aors = [
          ...aors
            .map((c) => ({
              label: c.name,
              value: c.id,
            }))
            .sort(dynamicSort('label', 1)),
        ];
      });
  }

  handleBusinessUnitChange() {
    this.selectedAORs = [];

    this.getBusinessUnits(this.businessUnitFilter()?.selectedBusinessUnits);
    this.refresh();
  }

  handleAOROrCompanyChange() {
    this.clearSelectedButtons();

    this.refresh();
  }

  getBusinessUnits(data: any) {
    this.selectedBusinessUnits = data;
    // need to delay this a tiny amount so that the selected business units update before we query
    setTimeout(() => {
      this.populateAors();
    });

    this.refresh();
  }

  todayClick() {
    this.todaySelected = true;
    this.tomorrowSelected = false;
    this.sevenDaysSelected = false;
    this.deratesSelected = false;
    this.pipelineMaintenanceSelected = false;

    let now = new Date();

    this.startDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0
    );
    this.endDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    );

    this.refresh();
  }

  tomorrowClick() {
    this.tomorrowSelected = true;
    this.sevenDaysSelected = false;
    this.deratesSelected = false;
    this.pipelineMaintenanceSelected = false;
    this.todaySelected = false;

    let now = new Date();
    let tomorrow = new Date(now.setDate(now.getDate() + 1));

    this.startDate = new Date(
      tomorrow.getFullYear(),
      tomorrow.getMonth(),
      tomorrow.getDate(),
      0,
      0,
      0
    );
    this.endDate = new Date(
      tomorrow.getFullYear(),
      tomorrow.getMonth(),
      tomorrow.getDate(),
      23,
      59,
      59
    );

    this.refresh();
  }

  sevenDayClick() {
    this.sevenDaysSelected = true;
    this.deratesSelected = false;
    this.pipelineMaintenanceSelected = false;
    this.todaySelected = false;
    this.tomorrowSelected = false;

    let nowToModify = new Date();
    let sevenDaysLater = new Date(
      nowToModify.setDate(nowToModify.getDate() + 7)
    );

    let now = new Date();

    this.startDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0
    );
    this.endDate = new Date(
      sevenDaysLater.getFullYear(),
      sevenDaysLater.getMonth(),
      sevenDaysLater.getDate(),
      23,
      59,
      59
    );

    this.refresh();
  }

  deratesClick() {
    this.deratesSelected = true;
    this.pipelineMaintenanceSelected = false;
    this.todaySelected = false;
    this.tomorrowSelected = false;
    this.sevenDaysSelected = false;

    this.refresh();
  }

  pipelineMaintenanceClick() {
    this.pipelineMaintenanceSelected = true;
    this.deratesSelected = false;
    this.todaySelected = false;
    this.tomorrowSelected = false;
    this.sevenDaysSelected = false;

    this.refresh();
  }

  clearTable() {
    this.clearSelectedButtons();

    this.isFirstLoad = true;
    this.selectedOutageGroups = [];
    this.selectedAORs = [];

    this.clearScreen();
  }

  clearSelectedButtons() {
    this.todaySelected = false;
    this.tomorrowSelected = false;
    this.sevenDaysSelected = false;
    this.deratesSelected = false;
    this.pipelineMaintenanceSelected = false;
  }

  refresh(): void {
    this.lazyLoad({
      lazy: this._reportSapOutageTableService.getLastLazyLoad()!,
    });
  }

  exportToCSV(): void {
    this.generalGrid()?.exportCSVFunction();
  }

  getExportFileName() {
    return `SAP Outages Report - ${new Date().toLocaleDateString()}`;
  }

  openDetailedOutage($event: any) {
    this.selectedOutage = $event.data;

    const readOnlyDetailedView = this.readOnlyDetailedView();
    if (readOnlyDetailedView) {
      this.detailDialogVisible = true;

      readOnlyDetailedView.notifNum = $event.data.notifNum;
      readOnlyDetailedView.loadData(this.selectedOutage!.id);
    }
  }
}
